import React, { useMemo, useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import { Loader2, AlertTriangle, Clock, User, CheckCircle, XCircle, CalendarDays } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { holidayAPI, leaveAPI } from '../../lib/api';

export function AddShiftDialog({
  open,
  onClose,
  onSubmit,
  formData,
  setFormData,
  selectedDate,
  staff,
  jobRoles,
  conflicts,
  submitting,
  getStaffAvailabilityForDay,
  schedules = [],
}) {
  const [notifyStaff, setNotifyStaff] = useState(true);
  const [holidayInfo, setHolidayInfo] = useState(null);
  const [leaveConflict, setLeaveConflict] = useState(null);

  // Non-blocking holiday warning — purely informational, never gates submit.
  // Resolves on the server (cached for 90s) so we never call Nager from the client.
  useEffect(() => {
    if (!open || !selectedDate) {
      setHolidayInfo(null);
      return undefined;
    }
    const dateStr = selectedDate.toISOString().split('T')[0];
    let cancelled = false;
    holidayAPI
      .resolve(dateStr)
      .then((r) => {
        if (!cancelled && r.data?.is_holiday) setHolidayInfo(r.data);
        else if (!cancelled) setHolidayInfo(null);
      })
      .catch(() => { /* silent — informational only */ });
    return () => { cancelled = true; };
  }, [open, selectedDate]);

  // Non-blocking leave-conflict warning: triggers when an employee+date is
  // chosen. Purely informational — schedule submission is NEVER blocked.
  useEffect(() => {
    if (!open || !selectedDate || !formData.user_id) {
      setLeaveConflict(null);
      return undefined;
    }
    const dateStr = selectedDate.toISOString().split('T')[0];
    let cancelled = false;
    leaveAPI
      .conflicts({ user_id: formData.user_id, date: dateStr })
      .then((r) => {
        if (cancelled) return;
        if (r.data?.on_leave || r.data?.status === 'pending') setLeaveConflict(r.data);
        else setLeaveConflict(null);
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [open, selectedDate, formData.user_id]);

  const selectedStaffAvailability = useMemo(() => {
    if (!formData.user_id || !selectedDate) return null;
    const dayOfWeek = selectedDate.getDay();
    const availability = getStaffAvailabilityForDay(formData.user_id, dayOfWeek);
    const staffMember = staff.find(s => s.id === formData.user_id);
    return { availability, staffName: staffMember?.name, wage: staffMember?.hourly_wage };
  }, [formData.user_id, selectedDate, staff, getStaffAvailabilityForDay]);

  // Calculate shift summary
  const shiftSummary = useMemo(() => {
    if (!formData.start_time || !formData.end_time) return null;

    const [startHour, startMin] = formData.start_time.split(':').map(Number);
    const [endHour, endMin] = formData.end_time.split(':').map(Number);

    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    let durationMinutes = endMinutes - startMinutes;

    if (durationMinutes <= 0) {
      durationMinutes += 24 * 60; // Handle overnight shifts
    }

    const durationHours = durationMinutes / 60;
    const hasBreak = durationHours > 6;
    const paidHours = hasBreak ? durationHours - 0.5 : durationHours;
    
    const wage = selectedStaffAvailability?.wage;
    const cost = wage ? paidHours * parseFloat(wage) : null;

    return {
      duration: durationHours,
      paidHours,
      hasBreak,
      cost,
    };
  }, [formData.start_time, formData.end_time, selectedStaffAvailability]);

  // Check if staff already has a shift on this day
  const hasExistingShift = useMemo(() => {
    if (!formData.user_id || !selectedDate || !schedules) return false;
    const dateStr = selectedDate.toISOString().split('T')[0];
    return schedules.some(shift => 
      shift.user_id === formData.user_id && 
      shift.date === dateStr
    );
  }, [formData.user_id, selectedDate, schedules]);

  // Time presets
  const applyPreset = (start, end) => {
    setFormData(prev => ({ ...prev, start_time: start, end_time: end }));
  };

  // Reset notify checkbox when dialog opens
  useEffect(() => {
    if (open) {
      setNotifyStaff(true);
    }
  }, [open]);

  // Enhanced submit handler with notify_staff flag
  const handleSubmitWithNotify = (e) => {
    e.preventDefault();
    // Add notify_staff to form data before submitting
    const enhancedFormData = { ...formData, notify_staff: notifyStaff };
    // Call parent's onSubmit with the event and enhanced data
    const enhancedEvent = { ...e, preventDefault: () => {} };
    onSubmit(enhancedEvent, enhancedFormData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Shift</DialogTitle>
          <DialogDescription>
            Schedule a shift for {selectedDate && formatDate(selectedDate)}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmitWithNotify} className="space-y-4">
          {/* Non-blocking holiday warning — informational only */}
          {holidayInfo && (
            <div
              className="flex items-start gap-2 p-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900"
              data-testid="holiday-warning-banner"
            >
              <CalendarDays className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <div className="text-sm leading-snug">
                <div className="font-medium">
                  {holidayInfo.holiday_name}
                  <span className="ml-2 text-xs font-normal text-amber-700/80">
                    {(holidayInfo.holiday_type || ['Public']).join(' · ')}
                  </span>
                </div>
                <div className="text-xs text-amber-700/90 mt-0.5">
                  Public holiday on this date{holidayInfo.is_paid === false ? ' (unpaid)' : ''}. Holiday pay rules may apply.
                </div>
              </div>
            </div>
          )}

          {/* Non-blocking leave conflict warning — informational only */}
          {leaveConflict && (leaveConflict.on_leave || leaveConflict.status === 'pending') && (
            <div
              className="flex items-start gap-2 p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-900"
              data-testid="leave-conflict-banner"
            >
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-rose-600" />
              <div className="text-sm leading-snug">
                <div className="font-medium">
                  {leaveConflict.on_leave
                    ? `Employee is on approved leave — ${leaveConflict.leave_type_name}`
                    : `Employee has a pending leave request — ${leaveConflict.leave_type_name}`}
                </div>
                <div className="text-xs text-rose-700/90 mt-0.5 font-mono">
                  {leaveConflict.start_date} → {leaveConflict.end_date}
                </div>
              </div>
            </div>
          )}

          {/* Staff selector */}
          <div className="space-y-2">
            <Label htmlFor="staff">Staff Member</Label>
            <Select
              value={formData.user_id}
              onValueChange={(value) => setFormData(prev => ({ ...prev, user_id: value }))}
            >
              <SelectTrigger data-testid="schedule-staff-select">
                <SelectValue placeholder="Select staff member" />
              </SelectTrigger>
              <SelectContent>
                {staff.map((member) => {
                  const avail = getStaffAvailabilityForDay(member.id, selectedDate?.getDay());
                  const isAvailable = !avail || avail.is_available;
                  const dateStr = selectedDate?.toISOString().split('T')[0];
                  const alreadyScheduled = schedules?.some(s => 
                    s.user_id === member.id && s.date === dateStr
                  );
                  
                  return (
                    <SelectItem key={member.id} value={member.id}>
                      <div className="flex items-center gap-2">
                        <span>{member.name}</span>
                        {alreadyScheduled ? (
                          <span className="text-amber-500" title="Already scheduled">⚠</span>
                        ) : avail ? (
                          isAvailable
                            ? <span className="text-green-500" title="Available">🟢</span>
                            : <span className="text-red-500" title="Not available">🔴</span>
                        ) : null}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {hasExistingShift && (
              <p className="text-sm text-amber-600 flex items-center gap-1">
                <AlertTriangle size={14} />
                This staff already has a shift that day
              </p>
            )}
          </div>

          {/* Staff availability info */}
          {selectedStaffAvailability && (
            <div className={`p-3 rounded-lg border ${
              selectedStaffAvailability.availability?.is_available
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center gap-2 text-sm">
                <User size={16} />
                <span className="font-medium">{selectedStaffAvailability.staffName}&apos;s Availability</span>
              </div>
              {selectedStaffAvailability.availability ? (
                selectedStaffAvailability.availability.is_available ? (
                  <div className="mt-1 text-sm text-green-700 flex items-center gap-1">
                    <Clock size={14} />
                    <span>
                      {selectedStaffAvailability.availability.start_time && selectedStaffAvailability.availability.end_time
                        ? `${selectedStaffAvailability.availability.start_time} - ${selectedStaffAvailability.availability.end_time}`
                        : 'Available all day'}
                    </span>
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-red-700">Not available this day</p>
                )
              ) : (
                <p className="mt-1 text-sm text-slate-500">No availability set</p>
              )}
            </div>
          )}

          {/* Quick time presets */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-500">Quick Presets</Label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => applyPreset('09:00', '17:00')}
                className="px-3 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50 transition-colors"
              >
                9–5
              </button>
              <button
                type="button"
                onClick={() => applyPreset('17:00', '23:00')}
                className="px-3 py-1 text-xs border border-slate-200 rounded hover:bg-slate-50 transition-colors"
              >
                5–11
              </button>
              <button
                type="button"
                className="px-3 py-1 text-xs border border-slate-200 rounded bg-slate-50 text-slate-400 cursor-default"
              >
                Custom
              </button>
            </div>
          </div>

          {/* Time inputs */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_time">Start Time</Label>
              <Input
                id="start_time"
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                required
                data-testid="schedule-start-time"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_time">End Time</Label>
              <Input
                id="end_time"
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                required
                data-testid="schedule-end-time"
              />
            </div>
          </div>

          {/* Live shift summary */}
          {shiftSummary && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="text-sm font-medium text-blue-900">
                {shiftSummary.duration.toFixed(1)}h shift
                {shiftSummary.hasBreak && ` • ${shiftSummary.paidHours.toFixed(1)}h paid`}
                {shiftSummary.cost && ` • $${shiftSummary.cost.toFixed(2)}`}
              </div>
              {shiftSummary.hasBreak && (
                <div className="text-xs text-blue-600 mt-1">
                  Includes 30 min unpaid break
                </div>
              )}
            </div>
          )}

          {/* Notify staff checkbox */}
          <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <input
              type="checkbox"
              id="notify_staff"
              checked={notifyStaff}
              onChange={(e) => setNotifyStaff(e.target.checked)}
              className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
            />
            <Label htmlFor="notify_staff" className="text-sm font-normal cursor-pointer">
              ☑ Notify staff (email + app)
            </Label>
          </div>

          {/* Role selection */}
          {jobRoles.length > 0 && (
            <div className="space-y-2">
              <Label>Job Role (optional)</Label>
              <Select
                value={formData.role_id}
                onValueChange={(value) => setFormData(prev => ({ ...prev, role_id: value === 'none' ? '' : value }))}
              >
                <SelectTrigger data-testid="schedule-role-select">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No role</SelectItem>
                  {jobRoles.map((role) => (
                    <SelectItem key={role.id} value={role.id}>
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: role.color }} />
                        <span>{role.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Conflict warnings */}
          {conflicts.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-800 font-medium text-sm mb-2">
                <AlertTriangle size={16} />
                Scheduling Conflicts Detected
              </div>
              <ul className="space-y-1">
                {conflicts.map((conflict, index) => (
                  <li key={index} className="text-sm text-amber-700 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                    {conflict.reason}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-amber-600 mt-2">
                You can still schedule this shift, but it may cause issues.
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className={`flex-1 ${conflicts.length > 0 ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
              disabled={submitting || !formData.user_id}
              data-testid="schedule-submit-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : conflicts.length > 0 ? (
                'Add Anyway'
              ) : (
                'Add Shift'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
